/**
 * IdentityStatusCard — Statut d'identité + CTA vers /verify-identity
 * ────────────────────────────────────────────────────────────────────
 * S'affiche dans le PriceProfileSheet. Récupère le statut via
 * GET /price/identity/status et affiche :
 *   - Badge vert "Identité vérifiée" si status=verified
 *   - Bloc jaune "En attente" si status=pending
 *   - Bloc rouge "À vérifier" si status=unverified / rejected + CTA
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const STATE_META = {
  unverified: {
    label: "Vérifier mon identité",
    hint: "Envoie ta pièce d'identité pour rassurer les autres.",
    bg: "#FFE9EC",
    fg: "#F45B73",
    icon: (props) => <AlertTriangle {...props} />,
  },
  pending: {
    label: "Vérification en cours",
    hint: "Notre équipe vérifie tes documents (~24 h).",
    bg: "#FFF3D9",
    fg: "#B57C00",
    icon: (props) => <Loader2 {...props} className={(props.className || "") + " animate-spin"} />,
  },
  verified: {
    label: "Identité vérifiée",
    hint: "Le badge est actif sur ton profil.",
    bg: "#DFF6E3",
    fg: "#1A8446",
    icon: (props) => <CheckCircle2 {...props} />,
  },
  rejected: {
    label: "Vérification refusée",
    hint: "Corrige tes documents et renvoie-les.",
    bg: "#FFE9EC",
    fg: "#F45B73",
    icon: (props) => <AlertTriangle {...props} />,
  },
};

export default function IdentityStatusCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/price/identity/status");
        if (alive) setStatus(r.data);
      } catch (_) {/*noop — silencieux, feature best-effort*/}
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !status) return null;
  // Carte masquée dès que la vérification est OK — plus propre côté
  // profil : moins de "noise" une fois l'étape terminée. L'utilisateur
  // conserve son badge "Identité vérifiée" ailleurs dans le profil.
  if (status.status === "verified") return null;
  const meta = STATE_META[status.status] || STATE_META.unverified;
  const Icon = meta.icon;
  const clickable = status.status !== "verified";

  const content = (
    <div
      data-testid="identity-status-card"
      className="w-full flex items-center gap-3 rounded-2xl bg-white p-4 border-2 border-black shadow-[0_3px_0_0_#000]"
      style={clickable ? undefined : { boxShadow: "0 3px 0 0 #000" }}
    >
      <div
        className="grid h-11 w-11 flex-none place-items-center rounded-xl border-2 border-black"
        style={{ background: meta.bg, color: meta.fg }}
      >
        {status.status === "verified" ? (
          <ShieldCheck className="h-5 w-5" strokeWidth={2.75} />
        ) : (
          <Icon className="h-5 w-5" strokeWidth={2.75} />
        )}
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <div className="font-[Outfit] text-[14px] font-black leading-none">
            {meta.label}
          </div>
          {status.status === "verified" && (
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 border border-black" title="Actif" />
          )}
        </div>
        <div className="mt-1 text-[11px] font-medium text-foreground/60 leading-snug">
          {meta.hint}
        </div>
      </div>
      {clickable && (
        <div className="text-foreground/40 flex-none">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </div>
      )}
    </div>
  );

  if (!clickable) return content;
  return (
    <Link
      to="/verify-identity"
      data-testid="identity-status-card-link"
      className="block active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all"
    >
      {content}
    </Link>
  );
}
